// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  HelpCircle,
  InfoIcon,
} from "lucide-react";
import { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTeamDisplayNames } from "@/hooks/api/use-team-display-names";
import { useTouchpointPersons } from "@/hooks/api/use-touchpoint-persons";
import { formatDate, formatFullDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import {
  channelColors,
  statusConfig,
} from "@/pages/accounts/account-explorer/touchpoints-table/constants";
import type {
  ActivityCellProps,
  ChannelCellProps,
  ExpandCellProps,
  PeopleCellProps,
  StatusCellProps,
  TeamCellProps,
} from "@/pages/accounts/account-explorer/touchpoints-table/types";
import {
  formatStatusText,
  getTeamVariant,
} from "@/pages/accounts/account-explorer/touchpoints-table/utils";

/**
 * Renders a cell displaying the channel information
 */
export const ChannelCell = memo(({ channel }: ChannelCellProps) => {
  // Handle multiple channels separated by comma
  const channels = channel ? channel.split(", ") : [];

  if (channels.length === 0) return null;

  // If multiple channels, use "Multiple" as the label with a tooltip
  const isMultiChannel = channels.length > 1;
  const displayChannel = isMultiChannel ? "Multiple" : channels[0];

  // For mixed channels, use a neutral gray color
  const channelKey = isMultiChannel
    ? "multiple"
    : displayChannel.toLowerCase().replace(/[\s_]+/g, "-");

  const channelInfo = channelColors[channelKey] || {
    color: "bg-muted-foreground/60 dark:bg-muted-foreground",
    ringColor: "ring-border/50 dark:ring-muted-foreground/20",
    label: displayChannel,
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      {isMultiChannel ? (
        // For multi-channel, show overlapping dots like a Venn diagram
        <div className="relative flex-shrink-0 w-3 h-2">
          {/* First dot */}
          <div
            className={cn(
              "absolute left-0 h-2 w-2 rounded-full ring-1 ring-offset-1 ring-offset-background",
              "bg-muted-foreground/50",
              "ring-muted-foreground/20",
              "z-10",
            )}
          />
          {/* Second dot */}
          <div
            className={cn(
              "absolute left-1 h-2 w-2 rounded-full ring-1 ring-offset-1 ring-offset-background",
              "bg-supporting-blue",
              "ring-supporting-blue/20",
              "z-0",
            )}
          />
        </div>
      ) : (
        // For single channel, show normal dot
        <div
          className={cn(
            "h-2 w-2 rounded-full ring-1 ring-offset-1 ring-offset-background flex-shrink-0",
            channelInfo.color,
            channelInfo.ringColor,
          )}
        />
      )}
      <span className="truncate">{channelInfo.label}</span>
      {isMultiChannel && (
        <InfoTooltip
          trigger={
            <InfoIcon className="h-3 w-3 text-supporting-blue flex-shrink-0" />
          }
        >
          <div className="space-y-1">
            {channels.map((chn, index) => (
              <div key={index}>{chn}</div>
            ))}
          </div>
        </InfoTooltip>
      )}
    </div>
  );
});

ChannelCell.displayName = "ChannelCell";

/**
 * Renders a cell displaying the status information
 */
export const StatusCell = memo(({ status }: StatusCellProps) => {
  // Handle multiple statuses separated by comma
  const statuses = status ? status.split(", ") : [];

  if (statuses.length === 0) return null;

  // If multiple statuses, use "Multiple" as the label with a tooltip
  const isMultiStatus = statuses.length > 1;
  const displayStatus = isMultiStatus ? "Multiple" : statuses[0];

  const statusKey = (displayStatus || "unknown").toLowerCase();
  const config = statusConfig[statusKey] || {
    icon: HelpCircle,
    color: "text-muted-foreground/60",
    label: formatStatusText(displayStatus),
  };
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-2 min-w-0">
      {isMultiStatus ? (
        // For multi-status, show overlapping dots like a Venn diagram
        <div className="relative flex-shrink-0 w-3 h-2">
          {/* First dot */}
          <div
            className={cn(
              "absolute left-0 h-2 w-2 rounded-full ring-1 ring-offset-1 ring-offset-background",
              "bg-muted-foreground/50",
              "ring-muted-foreground/20",
              "z-10",
            )}
          />
          {/* Second dot */}
          <div
            className={cn(
              "absolute left-1 h-2 w-2 rounded-full ring-1 ring-offset-1 ring-offset-background",
              "bg-supporting-blue",
              "ring-supporting-blue/20",
              "z-0",
            )}
          />
        </div>
      ) : (
        // For single status, show the icon
        <Icon className={cn("h-4 w-4 flex-shrink-0", config.color)} />
      )}
      <span className="truncate">{formatStatusText(displayStatus)}</span>
      {isMultiStatus && (
        <InfoTooltip
          trigger={
            <InfoIcon className="h-3 w-3 text-supporting-blue flex-shrink-0" />
          }
        >
          <div className="space-y-1">
            {statuses.map((stat, index) => (
              <div key={index}>{formatStatusText(stat)}</div>
            ))}
          </div>
        </InfoTooltip>
      )}
    </div>
  );
});

StatusCell.displayName = "StatusCell";

/**
 * Renders a cell displaying the people involved in an activity
 */
export const PeopleCell = memo(
  ({
    people,
    accountId,
    selectedPeople = [],
    getPersonNameOverride,
    getPersonEmailOverride,
  }: PeopleCellProps) => {
    const {
      getPersonName: getPersonNameFromApi,
      getPersonEmail: getPersonEmailFromApi,
      sortPeopleByTpRoleAndSelectionStatus,
      isLoading,
    } = useTouchpointPersons(
      getPersonNameOverride || getPersonEmailOverride ? undefined : accountId,
    );

    // Use overrides if provided, otherwise use API functions
    const getPersonName = getPersonNameOverride ?? getPersonNameFromApi;
    const getPersonEmail = getPersonEmailOverride ?? getPersonEmailFromApi;

    // Ensure people is an array and not empty
    if (!people || !Array.isArray(people) || people.length === 0) return null;

    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          {people.length > 1 && <Skeleton className="h-4 w-6" />}
        </div>
      );
    }

    // Sort people based on calculated sort order and selected status
    const sortedPeople = sortPeopleByTpRoleAndSelectionStatus(
      people,
      selectedPeople,
    );

    // Ensure we have at least one person to display
    if (sortedPeople.length === 0) return null;

    const firstPerson = sortedPeople[0];
    const firstPersonName = getPersonName(firstPerson.id);
    const firstPersonEmail = getPersonEmail(firstPerson.id);
    // Check if name is already an email (the fallback case)
    const isFirstPersonNameEmail = firstPersonName.includes("@");
    const remaining = sortedPeople.length - 1;
    const remainingPeople = sortedPeople.slice(1).map((person) => {
      const name = getPersonName(person.id);
      const email = getPersonEmail(person.id);
      const isNameEmail = name.includes("@");
      return {
        name,
        email,
        isNameEmail,
      };
    });

    return (
      <div className="flex items-center gap-1 min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate">{firstPersonName}</span>
          </TooltipTrigger>
          <TooltipContent>
            {isFirstPersonNameEmail
              ? "No additional info available"
              : firstPersonEmail || "No email available"}
          </TooltipContent>
        </Tooltip>
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-primary flex-shrink-0"> +{remaining}</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                {remainingPeople.map(({ name, email, isNameEmail }, index) => (
                  <div key={index}>
                    {name} {email && !isNameEmail ? `(${email})` : ""}
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  },
);

PeopleCell.displayName = "PeopleCell";

/**
 * Renders a cell displaying the teams involved in an activity
 */
export const TeamCell = memo(({ teamIds }: TeamCellProps) => {
  const { getTeamDisplayName, isLoading } = useTeamDisplayNames();

  if (!teamIds?.length) return null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-24" />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5 min-w-0">
      {teamIds.map((teamId) => {
        const displayName = getTeamDisplayName(teamId);
        const variant = getTeamVariant(displayName);
        return (
          <Badge
            key={teamId}
            variant={variant}
            className="truncate max-w-full shadow-sm hover:border-current/10"
          >
            {displayName}
          </Badge>
        );
      })}
    </div>
  );
});

TeamCell.displayName = "TeamCell";

/**
 * Renders a cell displaying the date information
 */
export const DateCell = ({ item, className, ...props }: ActivityCellProps) => (
  <TableCell
    className={cn(
      "w-[120px] py-3 px-2 border-b border-r border-border/50",
      className,
    )}
    {...props}
  >
    <div className="cell-content">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col min-w-0" data-timestamp={item.date}>
            {/* Show date range for collapsed groups */}
            {"activities" in item &&
            item.endDate !== item.date &&
            formatDate(item.date) !== formatDate(item.endDate) ? (
              <>
                <span className="truncate whitespace-nowrap text-xs">
                  {formatDate(item.date)}
                </span>
                <span className="truncate whitespace-nowrap text-[10px] text-muted-foreground">
                  to {formatDate(item.endDate)}
                </span>
              </>
            ) : (
              <span className="truncate whitespace-nowrap">
                {formatDate(item.date)}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {"activities" in item &&
          item.endDate !== item.date &&
          formatDate(item.date) !== formatDate(item.endDate) ? (
            <div className="space-y-1">
              <p>From: {formatFullDate(item.date)}</p>
              <p>To: {formatFullDate(item.endDate)}</p>
            </div>
          ) : (
            <p>{formatFullDate(item.date)}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </div>
  </TableCell>
);

/**
 * Renders a cell displaying the direction information
 */
interface GutterCellProps {
  isFirstInThread?: boolean;
  isLastInThread?: boolean;
  isInThread?: boolean;
  showThreadLine?: boolean;
}
export const GutterCell = ({
  isFirstInThread,
  isLastInThread,
  isInThread,
  showThreadLine,
}: GutterCellProps) => (
  <TableCell
    className={cn(
      "w-[20px] py-3 px-1 border-r border-border/50 relative bg-card",
    )}
    data-column-id="gutter"
  >
    {/* Thread connector line */}
    {(isInThread || showThreadLine) && (
      <div className="absolute inset-0">
        {showThreadLine && (
          <>
            {/* Use the thread direction to position the connecting lines */}
            <div className="absolute w-[2px] bg-muted-foreground/60 h-full left-[30%]" />
          </>
        )}
        {isInThread && (
          <>
            {/* Vertical line */}
            <div
              className={cn(
                "absolute w-[2px] bg-muted-foreground/60 left-[30%]",
                {
                  "top-0 h-[50%]": isLastInThread && !isFirstInThread,
                  "bottom-0 h-[50%]": isFirstInThread && !isLastInThread,
                  "h-full": !isFirstInThread && !isLastInThread,
                },
              )}
            />
            {/* Horizontal connector */}
            <div className="absolute h-[2px] bg-muted-foreground/60 top-[48%] left-[30%] w-[60%]" />
          </>
        )}
      </div>
    )}
  </TableCell>
);

/**
 * Renders a cell displaying the direction information
 */
export const DirectionCell = ({
  item,
}: Omit<ActivityCellProps, "className">) => (
  <TableCell
    className={cn(
      "w-[20px] py-3 px-0 relative",
      item.direction === "IN" && "bg-chart-5/10",
      item.direction === "OUT" && "bg-muted",
    )}
    data-column-id="direction"
  >
    {/* Direction indicator - only show IN direction by default, or OUT if part of current thread */}
    <div className="flex items-center justify-center h-full">
      <Tooltip>
        <TooltipTrigger asChild>
          {item.direction === "IN" ? (
            <ArrowRightCircle className="h-4 w-4 bg-chart-5 rounded-full text-background" />
          ) : (
            <ArrowLeftCircle className="h-4 w-4 bg-muted-foreground/40 rounded-full text-background" />
          )}
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {item.direction === "IN" ? "Engagement (incoming" : "Bid (outgoing"}{" "}
            touchpoint)
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  </TableCell>
);

/**
 * Renders a cell displaying the expand/collapse control for grouped activities
 */
export const ExpandCell = memo(
  ({ item, isExpanded, className, onClick }: ExpandCellProps) => (
    <TableCell
      className={cn("w-[30px] py-3 px-0 border-b border-border/50", className)}
      data-column-id="expand"
    >
      {"activities" in item ? (
        <div className="flex items-center justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(item.id);
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
          <span className="text-xs text-muted-foreground ml-0.5">
            {item.activities.length}
          </span>
        </div>
      ) : (
        <div className="w-4" />
      )}
    </TableCell>
  ),
);

ExpandCell.displayName = "ExpandCell";
